/**
 * `GET /v1/family/self` — parent-scoped introspection endpoint added in
 * plan 029. The PWA polls it at boot to decide whether to render the
 * email-verify banner + whether to show the Google-linked pill.
 *
 * State lives in a signal so the banner and the settings page both
 * subscribe cheaply.
 */

import { signal } from '@preact/signals';
import { api } from '../state/api';
import { authSession } from '../state/session';

export interface FamilySelf {
  parent_id: string;
  family_id: string;
  email: string;
  email_verified: boolean;
  has_password: boolean;
  has_firebase_link: boolean;
}

export const familySelf = signal<FamilySelf | null>(null);

/** Fetch + populate the signal. Silent on errors. */
export async function refreshFamilySelf(): Promise<void> {
  if (authSession.value === null) {
    familySelf.value = null;
    return;
  }
  try {
    const s = await api<FamilySelf>('/v1/family/self');
    familySelf.value = s;
  } catch {
    // Auth glitch / offline. Leave whatever's cached in the signal.
  }
}

/**
 * POST /v1/auth/email/verify-start.
 * Returns nothing on success — the server enqueues the code, the parent
 * gets an email, then calls `submitEmailVerifyCode` with the digits.
 */
export async function requestEmailVerifyCode(): Promise<void> {
  await api<{ sent?: boolean; already_verified?: boolean }>(
    '/v1/auth/email/verify-start',
    { method: 'POST' },
  );
}

/**
 * POST /v1/auth/email/verify-confirm. On 200 the CP flips
 * `email_verified` server-side; we refresh the local signal so
 * subscribers hide the banner.
 */
export async function submitEmailVerifyCode(code: string): Promise<void> {
  await api<{ verified: boolean }>('/v1/auth/email/verify-confirm', {
    method: 'POST',
    body: JSON.stringify({ code: code.trim() }),
  });
  await refreshFamilySelf();
}
